import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import { useEffect, useEffectEvent, useRef, useState } from "react"

export const TextVariants = cva("", {
  variants: {
    fontStyle: {
      DL: "display-large",
      DM: "display-medium",
      HL: "headline-large",
      HM: "headline-medium",
      HS: "headline-small",
      TL: "title-large",
      TM: "title-medium",
      TS: "title-small",
      BL: "body-large",
      BM: "body-medium",
      BS: "body-small",
      BXS: "body-extra-small",
      LL: "label-large",
      LM: "label-medium",
      LS: "label-small",
      LXS: "label-extra-small",
    },
  },
  defaultVariants: {
    fontStyle: "BM",
  },
})

export type TextProps = {
  className?: string
  readMore?: boolean // if true, show read more button, u need define a number of line
} & React.ComponentProps<"span"> &
  VariantProps<typeof TextVariants>

export function Text({ children, fontStyle, className, readMore, ...props }: TextProps) {
  const clampMatch = className?.match(/line-clamp-\d+/)
  const isLineClampPresent = !!clampMatch
  const [isClamp, setIsClamp] = useState(isLineClampPresent ? true : false)

  return (
    <>
      <span
        className={cn(TextVariants({ fontStyle }), className, !isClamp && isLineClampPresent && "line-clamp-none")}
        {...props}
      >
        {children}
      </span>
      {readMore && isLineClampPresent && (
        <div
          className="flex cursor-pointer items-center justify-center gap-2 font-semibold"
          onClick={() => {
            setIsClamp((prev) => !prev)
          }}
        >
          <span>{isClamp ? word("global.readMore") : word("global.close")}</span>
          {isClamp ? <ChevronDownIcon className="size-4" /> : <ChevronUpIcon className="size-4" />}
        </div>
      )}
    </>
  )
}

type TextWithTooltipProps = TextProps & {
  tooltipContent?: React.ReactNode
  tooltipProps?: React.ComponentProps<typeof Tooltip>
  tooltipTriggerProps?: React.ComponentProps<typeof TooltipTrigger>
  tooltipContentProps?: React.ComponentProps<typeof TooltipContent>
}

export function TextWithTooltip({
  tooltipContent,
  tooltipProps,
  tooltipTriggerProps,
  tooltipContentProps,
  title,
  children,
  className,
  ...props
}: TextWithTooltipProps) {
  const containerRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const checkTruncation = useEffectEvent(() => {
    if (containerRef.current) {
      const textElements = containerRef.current.querySelectorAll("[class*='truncate'], [class*='line-clamp']")
      for (const el of textElements) {
        if (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight) {
          setIsTruncated(true)
          return
        }
      }
      setIsTruncated(false)
    }
  })

  useEffect(() => {
    checkTruncation()
    window.addEventListener("resize", checkTruncation)
    return () => {
      window.removeEventListener("resize", checkTruncation)
    }
  }, [children, className])

  const defaultTooltipContent = tooltipContent ?? title ?? children

  const content = (
    <span ref={containerRef} className="inline-block min-w-0">
      <Text className={className} {...props}>
        {children}
      </Text>
    </span>
  )

  if (!isTruncated) {
    return content
  }

  return (
    <TooltipProvider delay={250}>
      <Tooltip {...tooltipProps}>
        <TooltipTrigger {...tooltipTriggerProps}>{content}</TooltipTrigger>
        <TooltipContent {...tooltipContentProps}>{defaultTooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
